import React, { useMemo, useState } from 'react'
import { useResolvedDarkMode } from '../hooks/useResolvedDarkMode'

interface TopTeacher {
  teacher: string
  count: number
}

interface StudentHeatmapData {
  id: string
  label: string
  dailyCounts: Record<string, number>
  topTeachers?: TopTeacher[]
}

interface ActivityHeatmapProps {
  rangeStart: string
  rangeEnd: string
  subject: string
  students: StudentHeatmapData[]
  classDailyTotals: Record<string, number>
  classAverageDailyTotals: Record<string, number>
  classTopTeachers?: TopTeacher[]
  loading?: boolean
}

// Build array of all weeks (Mon-Sun) covering the range
function buildWeeks(rangeStart: string, rangeEnd: string): Date[][] {
  const start = new Date(rangeStart)
  const end = new Date(rangeEnd)

  const firstMonday = new Date(start)
  const dayOfWeek = firstMonday.getDay()
  const offsetToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
  firstMonday.setDate(firstMonday.getDate() + offsetToMonday)
  firstMonday.setHours(0, 0, 0, 0)

  const weeks: Date[][] = []
  let cursor = new Date(firstMonday)

  while (cursor <= end) {
    const week: Date[] = []
    for (let d = 0; d < 7; d++) {
      week.push(new Date(cursor))
      cursor.setDate(cursor.getDate() + 1)
    }
    weeks.push(week)
  }

  return weeks
}

function toDateKey(date: Date): string {
  return date.toISOString().split('T')[0]
}

function isInRange(date: Date, rangeStart: string, rangeEnd: string): boolean {
  const key = toDateKey(date)
  return key >= rangeStart && key <= rangeEnd
}

function getCellColor(count: number, max: number, isWeekend: boolean, inRange: boolean, isDark = false): string {
  if (!inRange) return 'transparent'
  if (count === 0) {
    if (isDark) return isWeekend ? '#374151' : '#1f2937'
    return isWeekend ? '#f3f4f6' : '#f9fafb'
  }
  if (max === 0) return '#dbeafe'

  const ratio = Math.min(count / max, 1)
  if (ratio < 0.15) return '#dbeafe'
  if (ratio < 0.35) return '#93c5fd'
  if (ratio < 0.6) return '#3b82f6'
  if (ratio < 0.85) return '#1d4ed8'
  return '#1e3a8a'
}

function getClassCellColor(avg: number, maxAvg: number, isWeekend: boolean, inRange: boolean, isDark = false): string {
  if (!inRange) return 'transparent'
  if (avg === 0) {
    if (isDark) return isWeekend ? '#374151' : '#1f2937'
    return isWeekend ? '#f3f4f6' : '#f9fafb'
  }
  if (maxAvg === 0) return '#d1fae5'

  const ratio = Math.min(avg / maxAvg, 1)
  if (ratio < 0.15) return '#d1fae5'
  if (ratio < 0.35) return '#6ee7b7'
  if (ratio < 0.6) return '#10b981'
  if (ratio < 0.85) return '#047857'
  return '#064e3b'
}

const MEDAL_COLORS = ['#f59e0b', '#94a3b8', '#b45309'] // gold, silver, bronze
const MEDAL_LABELS = ['🥇', '🥈', '🥉']

const WEEKDAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

interface TooltipState {
  x: number
  y: number
  content: string
}

// ── Top-3-Lehrer Badge-Liste ────────────────────────────────────────────────
const TopTeacherBadges: React.FC<{ teachers: TopTeacher[]; totalBelege: number }> = ({
  teachers,
  totalBelege,
}) => {
  if (!teachers.length) return <span className="text-xs text-gray-300 dark:text-gray-600 italic">keine Daten</span>

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {teachers.map((t, i) => {
        const pct = totalBelege > 0 ? Math.round((t.count / totalBelege) * 100) : 0
        return (
          <span
            key={t.teacher}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-sm"
            title={`${t.teacher}: ${t.count} Belege (${pct}%)`}
          >
            <span>{MEDAL_LABELS[i]}</span>
            <span className="text-gray-700 dark:text-gray-300 dark:text-gray-600 max-w-[90px] truncate">{t.teacher}</span>
            <span style={{ color: MEDAL_COLORS[i] }} className="font-semibold">
              {t.count}
            </span>
          </span>
        )
      })}
    </div>
  )
}

// ── Heatmap-Grid ────────────────────────────────────────────────────────────
const HeatmapGrid: React.FC<{
  weeks: Date[][]
  dailyCounts: Record<string, number>
  maxCount: number
  rangeStart: string
  rangeEnd: string
  colorFn: (count: number, max: number, isWeekend: boolean, inRange: boolean, isDark?: boolean) => string
  tooltipPrefix?: string
  unit?: string
  isDark?: boolean
  cellStroke?: string
  axisLabelColor?: string
  weekdayLabelColor?: string
}> = ({ weeks, dailyCounts, maxCount, rangeStart, rangeEnd, colorFn, tooltipPrefix = '', unit = 'Belege', isDark = false, cellStroke = '#e5e7eb', axisLabelColor = '#6b7280', weekdayLabelColor = '#9ca3af' }) => {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)

  const monthLabels = useMemo(() => {
    const labels: Array<{ weekIdx: number; label: string }> = []
    let lastMonth = -1
    weeks.forEach((week, wIdx) => {
      const mon = week[0]
      if (isInRange(mon, rangeStart, rangeEnd) && mon.getMonth() !== lastMonth) {
        lastMonth = mon.getMonth()
        labels.push({
          weekIdx: wIdx,
          label: mon.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' }),
        })
      }
    })
    return labels
  }, [weeks, rangeStart, rangeEnd])

  const CELL = 14
  const GAP = 2
  const LEFT_PAD = 24
  const TOP_PAD = 20

  const gridWidth = weeks.length * (CELL + GAP) + LEFT_PAD
  const gridHeight = 7 * (CELL + GAP) + TOP_PAD

  return (
    <div className="relative select-none">
      <svg
        width={gridWidth}
        height={gridHeight}
        style={{ overflow: 'visible' }}
        onMouseLeave={() => setTooltip(null)}
      >
        {monthLabels.map(({ weekIdx, label }) => (
          <text key={`m-${weekIdx}`} x={LEFT_PAD + weekIdx * (CELL + GAP)} y={TOP_PAD - 5} fontSize={9} fill={axisLabelColor}>
            {label}
          </text>
        ))}

        {WEEKDAY_LABELS.map((lbl, dIdx) => (
          <text
            key={`wd-${dIdx}`}
            x={LEFT_PAD - 4}
            y={TOP_PAD + dIdx * (CELL + GAP) + CELL - 2}
            fontSize={8}
            fill={weekdayLabelColor}
            textAnchor="end"
          >
            {dIdx % 2 === 0 ? lbl : ''}
          </text>
        ))}

        {weeks.map((week, wIdx) =>
          week.map((day, dIdx) => {
            const key = toDateKey(day)
            const inRange = isInRange(day, rangeStart, rangeEnd)
            const count = dailyCounts[key] || 0
            const isWeekend = dIdx >= 5
            const fill = colorFn(count, maxCount, isWeekend, inRange, isDark)
            const cx = LEFT_PAD + wIdx * (CELL + GAP)
            const cy = TOP_PAD + dIdx * (CELL + GAP)

            return (
              <rect
                key={key}
                x={cx}
                y={cy}
                width={CELL}
                height={CELL}
                rx={2}
                fill={fill}
                stroke={inRange ? cellStroke : 'none'}
                strokeWidth={0.5}
                style={{ cursor: inRange && count > 0 ? 'pointer' : 'default' }}
                onMouseEnter={() => {
                  if (!inRange) return
                  const label = day.toLocaleDateString('de-DE', {
                    weekday: 'short',
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })
                  setTooltip({
                    x: cx + CELL + 4,
                    y: cy,
                    content: `${tooltipPrefix}${label}: ${count} ${unit}`,
                  })
                }}
              />
            )
          })
        )}

        {tooltip && (
          <g>
            <rect
              x={tooltip.x}
              y={tooltip.y - 4}
              width={tooltip.content.length * 6 + 12}
              height={20}
              rx={4}
              fill="#1f2937"
              opacity={0.9}
            />
            <text x={tooltip.x + 6} y={tooltip.y + 10} fontSize={10} fill="white">
              {tooltip.content}
            </text>
          </g>
        )}
      </svg>
    </div>
  )
}

// ── Kompakte Mini-Heatmap (eine Zelle pro Woche) ────────────────────────────
const MiniHeatmapRow: React.FC<{
  weeks: Date[][]
  dailyCounts: Record<string, number>
  maxCount: number
  rangeStart: string
  rangeEnd: string
  isDark?: boolean
}> = ({ weeks, dailyCounts, maxCount, rangeStart, rangeEnd, isDark = false }) => {
  const CELL = 10
  const GAP = 1
  const cellStroke = isDark ? '#4b5563' : '#e5e7eb'

  return (
    <svg width={weeks.length * (CELL + GAP)} height={CELL + 2} style={{ overflow: 'visible' }}>
      {weeks.map((week, wIdx) => {
        const weekDays = week.filter((d) => isInRange(d, rangeStart, rangeEnd))
        if (!weekDays.length) return null
        const weekTotal = weekDays.reduce((s, d) => s + (dailyCounts[toDateKey(d)] || 0), 0)
        const weekAvg = weekTotal / weekDays.length
        const fill = getCellColor(weekAvg, maxCount, false, true, isDark)
        return (
          <rect
            key={wIdx}
            x={wIdx * (CELL + GAP)}
            y={1}
            width={CELL}
            height={CELL}
            rx={2}
            fill={fill}
            stroke={cellStroke}
            strokeWidth={0.5}
          />
        )
      })}
    </svg>
  )
}

// ── Hauptkomponente ─────────────────────────────────────────────────────────
const ActivityHeatmap: React.FC<ActivityHeatmapProps> = ({
  rangeStart,
  rangeEnd,
  subject,
  students,
  classDailyTotals,
  classAverageDailyTotals,
  classTopTeachers = [],
  loading = false,
}) => {
  const isDark = useResolvedDarkMode()
  const [view, setView] = useState<'class' | 'students'>('class')
  const [expandedStudentId, setExpandedStudentId] = useState<string | null>(null)
  const cellStroke = isDark ? '#4b5563' : '#e5e7eb'
  const axisLabelColor = isDark ? '#9ca3af' : '#6b7280'
  const weekdayLabelColor = isDark ? '#6b7280' : '#9ca3af'

  const weeks = useMemo(() => buildWeeks(rangeStart, rangeEnd), [rangeStart, rangeEnd])

  const classMaxAvg = useMemo(
    () => Math.max(0, ...Object.values(classAverageDailyTotals)),
    [classAverageDailyTotals]
  )

  const studentMaxCounts = useMemo(() => {
    const map: Record<string, number> = {}
    for (const s of students) {
      map[s.id] = Math.max(0, ...Object.values(s.dailyCounts))
    }
    return map
  }, [students])

  const globalStudentMax = useMemo(
    () => Math.max(1, ...Object.values(studentMaxCounts)),
    [studentMaxCounts]
  )

  const classTotalBelege = useMemo(
    () => Object.values(classDailyTotals).reduce((s, v) => s + v, 0),
    [classDailyTotals]
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-500 dark:text-gray-400 dark:text-gray-500 space-x-2">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
        <span>Heatmap wird geladen…</span>
      </div>
    )
  }

  if (!students.length) {
    return <div className="text-center text-gray-400 dark:text-gray-500 py-8">Keine Schüler in dieser Klasse gefunden.</div>
  }

  const legendSteps = [0, 0.15, 0.35, 0.6, 0.85, 1.0]
  const legendColors =
    view === 'class'
      ? legendSteps.map((r) => getClassCellColor(r * classMaxAvg, classMaxAvg, false, true, isDark))
      : legendSteps.map((r) => getCellColor(r * globalStudentMax, globalStudentMax, false, true, isDark))

  return (
    <div className="space-y-4">
      {/* Header: Toggle + Legende */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 p-1 bg-gray-50 dark:bg-gray-800/50">
          <button
            type="button"
            onClick={() => setView('class')}
            className={`px-3 py-1 text-sm rounded transition ${
              view === 'class' ? 'bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-300 shadow-sm font-medium' : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            Klasse gesamt
          </button>
          <button
            type="button"
            onClick={() => setView('students')}
            className={`px-3 py-1 text-sm rounded transition ${
              view === 'students' ? 'bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-300 shadow-sm font-medium' : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            Alle Schüler
          </button>
        </div>

        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500">
          <span>wenig</span>
          {legendColors.map((c, i) => (
            <div
              key={i}
              style={{ width: 12, height: 12, backgroundColor: c, borderRadius: 2, border: `1px solid ${cellStroke}` }}
            />
          ))}
          <span>viel</span>
        </div>
      </div>

      {/* ── Klassen-Ansicht ── */}
      {view === 'class' && (
        <div className="space-y-4">
          {/* Top-3-Lehrer der Klasse */}
          {classTopTeachers.length > 0 && (
            <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide mb-2">
                Top-3 Lehrer der Klasse ({subject === 'ALL' ? 'alle Fächer' : subject})
              </p>
              <TopTeacherBadges teachers={classTopTeachers} totalBelege={classTotalBelege} />
            </div>
          )}

          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 mb-1">
              Ø Belege pro Schüler und Tag · {subject === 'ALL' ? 'alle Fächer' : subject}
            </p>
            <div className="overflow-x-auto pb-2">
              <HeatmapGrid
                weeks={weeks}
                dailyCounts={classAverageDailyTotals}
                maxCount={classMaxAvg}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                colorFn={getClassCellColor}
                tooltipPrefix="Ø "
                unit="Belege/Schüler"
                isDark={isDark}
                cellStroke={cellStroke}
                axisLabelColor={axisLabelColor}
                weekdayLabelColor={weekdayLabelColor}
              />
            </div>
          </div>

          <div className="border-t pt-3">
            <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 mb-1">Summe aller Belege der Klasse pro Tag</p>
            <div className="overflow-x-auto pb-2">
              <HeatmapGrid
                weeks={weeks}
                dailyCounts={classDailyTotals}
                maxCount={Math.max(1, ...Object.values(classDailyTotals))}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                colorFn={getClassCellColor}
                tooltipPrefix=""
                unit="Belege (gesamt)"
                isDark={isDark}
                cellStroke={cellStroke}
                axisLabelColor={axisLabelColor}
                weekdayLabelColor={weekdayLabelColor}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Schüler-Ansicht ── */}
      {view === 'students' && (
        <div className="space-y-1">
          <p className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 mb-2">
            Farbskala einheitlich über alle Schüler (max {globalStudentMax} Belege/Tag). Klicke auf einen Schüler für Details.
          </p>
          {students.map((student) => {
            const isExpanded = expandedStudentId === student.id
            const maxForStudent = studentMaxCounts[student.id] || 0
            const totalBelege = Object.values(student.dailyCounts).reduce((s, v) => s + v, 0)
            const topTeachers = student.topTeachers ?? []

            return (
              <div
                key={student.id}
                className={`rounded-lg border transition ${
                  isExpanded ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/30' : 'border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-800/50'
                }`}
              >
                {/* Zeilen-Header */}
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 flex items-center gap-3 flex-wrap"
                  onClick={() => setExpandedStudentId(isExpanded ? null : student.id)}
                >
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200 w-40 shrink-0 truncate">
                    {student.label}
                  </span>

                  {/* Mini-Heatmap inline */}
                  {!isExpanded && (
                    <div className="shrink-0">
                      <MiniHeatmapRow
                        weeks={weeks}
                        dailyCounts={student.dailyCounts}
                        maxCount={globalStudentMax}
                        rangeStart={rangeStart}
                        rangeEnd={rangeEnd}
                        isDark={isDark}
                      />
                    </div>
                  )}

                  <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
                    {totalBelege} Belege · max {maxForStudent}/Tag
                  </span>

                  {/* Top-3-Lehrer inline (kompakt) */}
                  {topTeachers.length > 0 && (
                    <div className="flex items-center gap-1 ml-auto shrink-0" onClick={(e) => e.stopPropagation()}>
                      <TopTeacherBadges teachers={topTeachers} totalBelege={totalBelege} />
                    </div>
                  )}

                  <span className="text-gray-400 dark:text-gray-500 text-xs ml-2 shrink-0">{isExpanded ? '▲' : '▼'}</span>
                </button>

                {/* Ausgeklappte Heatmap */}
                {isExpanded && (
                  <div className="px-3 pb-4 space-y-3">
                    <div className="overflow-x-auto">
                      <HeatmapGrid
                        weeks={weeks}
                        dailyCounts={student.dailyCounts}
                        maxCount={globalStudentMax}
                        rangeStart={rangeStart}
                        rangeEnd={rangeEnd}
                        colorFn={getCellColor}
                        unit="Belege"
                        isDark={isDark}
                        cellStroke={cellStroke}
                        axisLabelColor={axisLabelColor}
                        weekdayLabelColor={weekdayLabelColor}
                      />
                    </div>

                    {/* Top-3-Lehrer detailliert */}
                    {topTeachers.length > 0 && (
                      <div className="p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">
                          Top-3 Lehrer für {student.label}
                        </p>
                        <div className="flex flex-col gap-2">
                          {topTeachers.map((t, i) => {
                            const pct = totalBelege > 0 ? Math.round((t.count / totalBelege) * 100) : 0
                            const barWidth = pct
                            return (
                              <div key={t.teacher} className="flex items-center gap-2">
                                <span className="text-base w-6 shrink-0">{MEDAL_LABELS[i]}</span>
                                <span className="text-sm text-gray-700 dark:text-gray-300 dark:text-gray-600 w-36 truncate shrink-0">{t.teacher}</span>
                                <div className="flex-1 h-4 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full transition-all"
                                    style={{
                                      width: `${barWidth}%`,
                                      backgroundColor: MEDAL_COLORS[i],
                                    }}
                                  />
                                </div>
                                <span className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 w-20 text-right shrink-0">
                                  {t.count} ({pct}%)
                                </span>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ActivityHeatmap
